import type { Permission } from '@af/shared';

export interface NavItem {
  to:
    | '/'
    | '/chat'
    | '/master'
    | '/tasks'
    | '/runs'
    | '/flows'
    | '/skills'
    | '/approvals'
    | '/run-analysis'
    | '/usage'
    | '/notifications'
    | '/help'
    | '/settings';
  key: string;
  /** Second key of the `g <key>` shortcut. */
  hotkey: string;
  perm?: Permission;
  badge?: 'approvals' | 'notifications';
}

/** All destinations (palette + `g` shortcuts). */
export const NAV: NavItem[] = [
  { to: '/', key: 'nav.home', hotkey: 'g' },
  { to: '/master', key: 'nav.master', hotkey: 'm', perm: 'conversation:read' },
  // One page for the company: every agent at a glance, channels, DMs and your chats.
  { to: '/chat', key: 'nav.company', hotkey: 'c', perm: 'conversation:read' },
  { to: '/tasks', key: 'nav.tasks', hotkey: 't', perm: 'task:read' },
  { to: '/runs', key: 'nav.runs', hotkey: 'r', perm: 'run:read' },
  { to: '/flows', key: 'nav.flows', hotkey: 'f', perm: 'flow:read' },
  { to: '/skills', key: 'nav.skills', hotkey: 'k', perm: 'skill:read' },
  { to: '/approvals', key: 'nav.approvals', hotkey: 'p', perm: 'approval:read', badge: 'approvals' },
  { to: '/run-analysis', key: 'nav.runAnalysis', hotkey: 'd', perm: 'analysis:read' },
  { to: '/usage', key: 'nav.usage', hotkey: 'u', perm: 'usage:read' },
  {
    to: '/notifications',
    key: 'nav.notifications',
    hotkey: 'n',
    perm: 'notification:read',
    badge: 'notifications',
  },
  { to: '/help', key: 'nav.help', hotkey: 'e' },
  { to: '/settings', key: 'nav.settings', hotkey: 's' },
];

/** Sidebar primary rows (icon + label): the daily work. */
export const PRIMARY = NAV.filter((n) =>
  // The Company chat lives on the company page (pinned at the top of its list), not in the sidebar.
  ['/', '/chat', '/tasks', '/runs', '/approvals'].includes(n.to),
);
/** Sidebar "Workspace" rows: recorded procedures, skills, spend and help. */
export const WORKSPACE = NAV.filter((n) =>
  ['/flows', '/skills', '/usage', '/run-analysis', '/help'].includes(n.to),
);

/** Top-bar page title for a pathname (i18n key). */
export function titleKeyFor(pathname: string): string {
  if (pathname === '/') return 'nav.home';
  if (pathname === '/agents/new') return 'pages.newAgent';
  if (pathname === '/onboarding') return 'onboarding.title';
  if (pathname === '/companies') return 'companies.title';
  const hit = NAV.filter((n) => n.to !== '/').find(
    (n) => pathname === n.to || pathname.startsWith(`${n.to}/`),
  );
  return hit?.key ?? 'nav.home';
}
